import React, { useState, useEffect, useContext } from "react";
import { Card, Table, Button, Form, OverlayTrigger, Tooltip} from "react-bootstrap";
import NotyfContext from "../contexts/NotyfContext";
import { X, Check, Edit2, Trash2, MessageSquare, File, Watch} from "react-feather";
import { UserSettingsContext } from "../contexts/UserSettingsContext";
import { useTable, useSortBy, useFilters} from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";

import authenticatedFetch from "../utils/fetch"
import EditableExpires from "./EditableExpires";
import UsStateSelect from "./UsStateSelect";

import rawOtherCertificationTypes from "./otherCertificationTypes"
import LinkedDocumentsDisplay from "./LinkedDocumentsDisplay";
import LinkedDocumentSelect from "./LinkedDocumentSelect";
import { getDocumentDisplayLabel } from "./linkedDocumentUtils";
import { customExpiresFilter, ExpiresColumnCheckboxFilter, caseInsensitiveSort, getReminderSentDisplay } from "./reactTableUtils";

const OtherCertification = ({ id, providerId, state, otherCertificationType, effectiveDate, expirationDate, continuous, number, notRenewed, reminderSent, documents, refreshList, canEdit, onCancelEdit, startInEditMode=false }) => {
  const [editing, setEditing] = useState(startInEditMode);
  const [localState, setLocalState] = useState(state);
  const [localOtherCertificationType, setLocalOtherCertificationType] = useState(otherCertificationType);
  const [localNumber, setLocalNumber] = useState(number);
  const [localEffectiveDate, setLocalEffectiveDate] = useState(effectiveDate);
  const [localExpirationDate, setLocalExpirationDate] = useState(expirationDate);
  const [localContinuous, setLocalContinuous] = useState(continuous);
  const [localNotRenewed, setLocalNotRenewed] = useState(notRenewed);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [documentFilenames, setDocumentFilenames] = useState([]);

  const { isH3Admin, authorities, documentsRefresh, setDocumentsRefresh } = useContext(UserSettingsContext);
  
  const notyf = useContext(NotyfContext);

  const setInitialDocumentFilenames = () => {
    let initialDocumentFilenames = []
    let initialSelectedDocuments = []
    if (documents) // undefined if we're excluding the column due to permissions
    {
      for (var i=0; i<documents.length; i++)
      {
        initialDocumentFilenames.push(documents[i].filename);
        initialSelectedDocuments.push({value: documents[i].filename, label: getDocumentDisplayLabel(documents[i])});
      }
    }

    setDocumentFilenames(initialDocumentFilenames);
    setSelectedDocuments(initialSelectedDocuments);
  }

  useEffect(() => {
      setInitialDocumentFilenames();
  }, [id, providerId, documents]);

  useEffect(() => {
    if (!editing) {
      setLocalNumber(number);
      setLocalEffectiveDate(effectiveDate);
      setLocalExpirationDate(expirationDate);
      setLocalContinuous(continuous);
      setLocalNotRenewed(notRenewed);
      setInitialDocumentFilenames();
    }
  }, [effectiveDate, expirationDate, continuous, number, notRenewed, documents]);

  const handleDocumentChanged = (documents, e) => {
    setSelectedDocuments(documents);

    let newDocumentFilenames = []
    for (var i=0; i<documents.length; i++)
    {
      newDocumentFilenames.push(documents[i].value);
    }

    setDocumentFilenames(newDocumentFilenames);
  }

  function handleDeleteClicked(e) {
    e.preventDefault();
    if (window.confirm("Are you sure you want to delete this item?"))
    {
      authenticatedFetch({
        method: "DELETE",
        path: "othercertification/" + id,
        successHandler: function(result) {
          refreshList();
          setDocumentsRefresh(documentsRefresh + 1);
          notyf.open({type: "success", message: "Deleted!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
        },
        errorHandler: function(error) {
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
      });
    }
  }

  function handleEditClicked(e) {
    e.preventDefault();
    setEditing(true);
  }

  function cancelEdit(e) {
    e.preventDefault();
    setEditing(false);
    setLocalState(state);
    setLocalOtherCertificationType(otherCertificationType);
    setLocalNumber(number);
    setLocalEffectiveDate(effectiveDate);
    setLocalExpirationDate(expirationDate);
    setLocalContinuous(continuous);
    setLocalNotRenewed(notRenewed);
    setInitialDocumentFilenames();

    if (onCancelEdit)
    {
      onCancelEdit();
    }
  }

  function saveEdit(e) {
    e.preventDefault();
    if (localNumber === '')
    {
      alert("Please enter number");
      return;
    }

    setEditing(false);

    authenticatedFetch({
      method: id ? "PUT" : "POST",
      requestData: {providerId: parseInt(providerId), state: localState, otherCertificationType: localOtherCertificationType, number: localNumber, effectiveDate: localEffectiveDate, expirationDate: localExpirationDate, continuous: isTest && !!localContinuous, notRenewed: localNotRenewed, documentFilenames: documentFilenames},
      path: id ? "othercertification/" + id  : "othercertification",
      successHandler: function(result) {
        refreshList();
        setDocumentsRefresh(documentsRefresh + 1);
        notyf.open({type: "success", message: id ? "Updated!" : "Saved!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
      },
      errorHandler: function() {
        setEditing(true);
        setLocalState(state);
        setLocalOtherCertificationType(otherCertificationType);
        setLocalEffectiveDate(effectiveDate);
        setLocalExpirationDate(expirationDate);
        setLocalNumber(number);
        setLocalContinuous(continuous);
        setLocalNotRenewed(notRenewed);
        setInitialDocumentFilenames();
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }


  function handleStateChanged(name, newValue) {
    setLocalState(newValue);
  }
  
  function handleOtherCertificationTypeChanged(e) {
    e.preventDefault();
    setLocalOtherCertificationType(e.target.value);
    if (e.target.value !== "TEST")
    {
      setLocalContinuous(false);
    }
  }

  function handleNumberChanged(e) {
    e.preventDefault();
    setLocalNumber(e.target.value);
  }

  function handleEffectiveDateChanged(val, e) {
    e.preventDefault();
    setLocalEffectiveDate(val);
  }

  function handleExpirationDateChanged(val, e) {
    e.preventDefault();
    setLocalExpirationDate(val);
  }

  function handleContinuousChanged(e) {
    setLocalContinuous(e.target.checked);
    if (e.target.checked)
    {
      setLocalExpirationDate(null);
    }
  }

  function handleNotRenewedChanged(e) {
    setLocalNotRenewed(e.target.checked);
  }

  const isTest = localOtherCertificationType === "TEST";

  let otherCertificationTypeList = rawOtherCertificationTypes.map((otherCertificationType, i) => {
    return (
      <option key={i} value={otherCertificationType.value}>{otherCertificationType.label}</option>
    )
  }, this);

  var selectedOtherCertificationTypeLabel = '';
  if (localOtherCertificationType !== '')
  {
      for (let i = 0; i < rawOtherCertificationTypes.length; i++)
      {
          if (localOtherCertificationType === rawOtherCertificationTypes[i].value)
          {
            selectedOtherCertificationTypeLabel = rawOtherCertificationTypes[i].label;
            break;
          }
      }
  }

  return (
    <>
    {editing ?
    <td style={{minWidth: '90px'}}>
      <UsStateSelect originalValue={localState} name="numberState" onValueChanged={handleStateChanged} small={true}/>
    </td>
    :
    <td>
      {localState}
    </td>
    }
    {editing ?
    <td>
      <Form.Select size="sm" value={localOtherCertificationType} onChange={(e) => {handleOtherCertificationTypeChanged(e)}} style={{width: "auto"}} >
          {otherCertificationTypeList}
      </Form.Select>
    </td>
      :
    <td style={
      !selectedOtherCertificationTypeLabel || selectedOtherCertificationTypeLabel.length === 0
        ? {
            backgroundColor: "rgba(220,53,69,0.3)",
            // cancel the striped bg for this cell
            ["--bs-table-accent-bg"]: "transparent"
          }
        : {}
      }>
        {selectedOtherCertificationTypeLabel}
      </td>
    }
    {editing ?
    <td>
      <Form.Control size="sm" style={{minWidth: '80px'}} maxLength={255} value={localNumber} onChange={(e) => {handleNumberChanged(e)}} />
    </td>
    :
    <td style={
      !localNumber || localNumber.length === 0
        ? {
            backgroundColor: "rgba(220,53,69,0.3)",
            // cancel the striped bg for this cell
            ["--bs-table-accent-bg"]: "transparent"
          }
        : {}
    }>
      {localNumber}
    </td>
    }
    <td>
    <EditableExpires warnWithColors={false} localExpires={localEffectiveDate} localActive={true} editingExpires={editing} handleExpiresChanged={handleEffectiveDateChanged} blankText=""/>
    </td>
    <td
      style={
        (!localExpirationDate || localExpirationDate.length === 0) && !(isTest && localContinuous)
          ? {
              backgroundColor: "rgba(220,53,69,0.3)",
              // cancel the striped bg for this cell
              ["--bs-table-accent-bg"]: "transparent"
            }
          : {}
    }>
    <div className="d-flex align-items-center">
      <EditableExpires localExpires={localExpirationDate} localActive={!localContinuous} editingExpires={editing} handleExpiresChanged={handleExpirationDateChanged} blankText={isTest && localContinuous ? "n/a" : ""}/>
      {editing && isTest &&
        <Form.Check type="checkbox" id={"continuous-" + (id || "new")} className="d-flex align-items-center gap-1 ms-2 mb-0 ps-0 text-nowrap" style={{minHeight: 0, fontSize: "0.85em"}}>
          <Form.Check.Input type="checkbox" className="m-0 float-none" checked={!!localContinuous} onChange={(e) => {handleContinuousChanged(e)}} />
          <Form.Check.Label className="mb-0">n/a</Form.Check.Label>
        </Form.Check>
      }
    </div>
    </td>
    {editing ?
    <td>
      <Form.Check size="sm" checked={localNotRenewed} onChange={(e) => {handleNotRenewedChanged(e)}} />
    </td>
      :
    <td>
        {localNotRenewed ? <Watch style={{'height': '16px', 'width': '16px'}}/> : <>&nbsp;</>}
    </td>
    }
    {(isH3Admin || (authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")))&&
    <>
      {editing && (isH3Admin || authorities.includes("PROVIDER_DOCUMENT_EDIT"))?
      <td>
        <LinkedDocumentSelect entityType={"othercertification"} providerId={providerId} handleDocumentChanged={handleDocumentChanged} selectedDocuments={selectedDocuments}/>
      </td>
        : 
      <td>
        <LinkedDocumentsDisplay entityType={"othercertification"} documents={documents} entityId={id}/>
      </td> 
      }
    </>
    }
    {<td>{getReminderSentDisplay(reminderSent)}</td>}
    <td style={{width: '70px', whiteSpace: 'nowrap'}}>
      { canEdit?
        <>
        {editing ? 
        <>
          <X style={{'height': '16px', 'width': '16px'}} cursor="pointer" onClick={(e) => {cancelEdit(e)}} />
          &nbsp;<Check style={{'height': '16px', 'width': '16px'}} cursor="pointer" onClick={(e) => {saveEdit(e)}} className="text-success"/>
        </>
        :
        <>
          <Edit2 style={{'height': '12px', 'width': '12px'}} cursor="pointer" onClick={(e) => {handleEditClicked(e)}}/>&nbsp;&nbsp;
          <Trash2 style={{'height': '12px', 'width': '12px'}} cursor="pointer" onClick={(e) => {handleDeleteClicked(e)}}/>
        </>
        }
        </>
        : <>&nbsp;</>
      }
    </td>
  </>
  )
}


const OtherCertificationsTable = ({providerId, addingNew, onCancelAddNew, list, isLoaded, error, canEdit, refreshList}) => {
  const data = list;
  
  const { isH3Admin, authorities } = useContext(UserSettingsContext);

  const columns = React.useMemo(() => {
    const baseColumns = [
      {
        accessor: "id",
        key: "id",
      },
      {
        Header: "State",
        accessor: "state",
        key: "state",
      },
      {
        Header: "Type",
        accessor: "otherCertificationType",
        key: "otherCertificationType",
      },
      {
        Header: "Number",
        accessor: "number",
        key: "number",
      },
      {
        Header: "Issue Date",
        accessor: "effectiveDate",
        key: "effectiveDate",
      },
      {
        Header: "Expiration",
        accessor: "expirationDate",
        key: "expirationDate",
        Filter: ExpiresColumnCheckboxFilter,
        filter: customExpiresFilter("expirationDate")
      },
      {
        Header: "Not Renewed",
        accessor: "notRenewed",
        key: "notRenewed",
      },
    ];

    if(isH3Admin || (authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")))
    {
      baseColumns.push(
      {
        Header: () => (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Documents</Tooltip>}
          >
            <span>
              <File size={16} />
            </span>
          </OverlayTrigger>
        ),
        accessor: "documents",
        key: "documents",
      });
    }

    baseColumns.push(
      {
        Header: () => (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Reminder Sent</Tooltip>}
          >
            <span>
              <MessageSquare size={16} />
            </span>
          </OverlayTrigger>
        ),
        accessor: "reminderSent",
        key: "reminderSent",
      },
      {
        Header: "",
        accessor: "icons",
        key: "icons",
      },
    );

    return baseColumns;
  }, [isH3Admin, authorities]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
  useTable(
    {
      columns,
      data,
      autoResetSortBy: false,
      autoResetFilters: false,
      initialState: {
        hiddenColumns: ['id'],
        sortBy: [
            {
                id: 'state',
                desc: false
            }
        ],
        filters: [
          {
              id: 'expirationDate',
              value: 'excludeGreaterThan30Days',
          },
        ],
      },
      sortTypes: {
        alphanumeric: (row1, row2, columnName) => caseInsensitiveSort({row1, row2, columnName})
      }
    },
    useFilters,
    useSortBy,
  );

  // Extract the expires filter
  var expiresFilter = "";
  for (var i=0; i < headerGroups.length; i++)
  {
    for (var j=0; j < headerGroups[i].headers.length; j++)
    {
      var column = headerGroups[i].headers[j];
      if (column.id === "expirationDate")
      {
        expiresFilter = column.render("Filter");
        break;
      }
    }
  }

  return (
    <>
      <Table className="table-sm" striped bordered hover {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()} style={{whiteSpace: "nowrap"}}>
            {headerGroup.headers.map((column) => {
              const { key, ...restHeaderProps } = (column.id === "icons" || column.id === "reminderSent" || column.id === "documents") ? column.getHeaderProps() : column.getHeaderProps(column.getSortByToggleProps());
              return (
                <th {...restHeaderProps} key={key} style={column.id === "notRenewed" ? {...restHeaderProps.style, whiteSpace: "normal"} : restHeaderProps.style}>
                  {column.render("Header")}
                  {column.id !== "icons" && column.id !== "reminderSent" && column.id !== "documents" ? (
                    <>
                    {column.isSorted ? (
                        column.isSortedDesc ? (
                          <FontAwesomeIcon icon={faSortDown} className="ms-2" />
                        ) : (
                          <FontAwesomeIcon icon={faSortUp} className="ms-2" />
                        )
                      ) : (
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                    )}
                    </>
                  ) : <></>
                  }
                </th>
              )
            }
            )}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {
        error ? (<tr><td colSpan={9}>Something went wrong; please reload the page...</td></tr>) : ( 
          !isLoaded ? (<tr><td colSpan={9}>Loading...</td></tr>) : (
            rows.map((row) => {
            prepareRow(row);
            const { key, ...restRowProps } = row.getRowProps();
            return (
              <tr {...restRowProps} key={key} 
                style={{
                  ...(row.values.notRenewed ? {opacity: 0.5} : {}),
                  ...(row.values.number && row.values.number.toLowerCase().indexOf("please verify") >= 0
                    ? {
                        // td's read these from the tr; setting them here colors the whole row
                        ["--bs-table-bg"]: "rgba(255,193,7,0.3)",
                        // cancel the striped overlay so it doesn't stack on the warning color
                        ["--bs-table-striped-bg"]: "transparent",
                        ["--bs-table-accent-bg"]: "transparent"
                      }
                    : {})
                }}>
                <OtherCertification providerId={providerId} id={row.values.id} key={row.values.id} state={row.values.state ? row.values.state : ""} otherCertificationType={row.values.otherCertificationType ? row.values.otherCertificationType : ""} number={row.values.number ? row.values.number : ""} effectiveDate={row.values.effectiveDate} expirationDate={row.values.expirationDate} continuous={row.original.continuous == null ? false : row.original.continuous} notRenewed={row.values.notRenewed} reminderSent={row.values.reminderSent} documents={row.values.documents} refreshList={refreshList} canEdit={canEdit}/>
              </tr>
            );
          })))
        }
        {
        addingNew ?
          <tr><OtherCertification providerId={providerId} key={0} startInEditMode={true} state={""} otherCertificationType={""} number={""}  effectiveDate={null} expirationDate={null} continuous={false} notRenewed={false} reminderSent={""} documents={[]} onCancelEdit={onCancelAddNew} canEdit={canEdit} refreshList={refreshList}/></tr>
        : <></>
        }
      </tbody>
    </Table>
    {expiresFilter}
  </>
  )
}

const OtherCertifications = ({providerId, canEdit}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [addingNew, setAddingNew] = useState(false);

  const { documentsRefresh, tasksRefresh } = useContext(UserSettingsContext);

  useEffect(() => {
    refreshList();
  }, [providerId, documentsRefresh, tasksRefresh])

  function refreshList()
  {
    setAddingNew(false);
    authenticatedFetch({
      path: "othercertification/provider/" + providerId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function() {
        setIsLoaded(true);
        setError(true);
      }
    });
  }

  const handleAddNewClicked = (e) => {
    e.preventDefault();
  
    setAddingNew(true);
  }

  const onCancelAddNew = () => {
    setAddingNew(false);
  }

  var list = [];
  if (isLoaded)
  {
    list = data.map((item, i) => {
      return {key: item.id, id: item.id, state: item.state, otherCertificationType: item.otherCertificationType, number: item.number, effectiveDate: item.effectiveDate, expirationDate: item.expirationDate, continuous: item.continuous, notRenewed: item.notRenewed, reminderSent: item.reminderSent, documents: item.documents, updated: item.updated, updatedBy: item.updatedBy}
    }, this);
  }

  return (
    <Card>
      <Card.Header style={{marginBottom: '-20px'}}>
        <Card.Title>Other Certifications
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <OtherCertificationsTable list={list} addingNew={addingNew} onCancelAddNew={onCancelAddNew} providerId={providerId} error={error} isLoaded={isLoaded} canEdit={canEdit} refreshList={refreshList}/>
        {canEdit?
          <span className="float-end" style={{marginTop: '6px'}}>
            <Button size="sm" variant="primary" className="float-end" onClick={(e) => {handleAddNewClicked(e)}}>+ Add New Certification</Button>
          </span>
          : <></>
          }
      </Card.Body>
    </Card>
  )
}

export default OtherCertifications;
